// ─── Ajout / édition d'une GARDE VACANCES (schéma-période) — modale ──
// Période { début, fin|sans fin } + schéma de répartition : qui a la 1re moitié
// les années paires / impaires (alternance annuelle) + veille au soir.
// Rendue en SŒUR (calque) depuis FeuilleGardes. Renvoie { ok, err } via onValider.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useUI, useT, Feuille, SelecteurDate, ChoixPuces, Bascule, frJMA } from '@mop/core';

const autre = (id) => (id === 'parent1' ? 'parent2' : 'parent1');

export default function FeuilleGardeVacances({ visible, mode, init, parents, onValider, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const nomG = (g) => (g === 'parent1' ? (parents?.parent1?.nom || t('garde.parent1')) : (parents?.parent2?.nom || t('garde.parent2')));
  const opts = [{ id: 'parent1', label: nomG('parent1') }, { id: 'parent2', label: nomG('parent2') }];

  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');
  const [pair, setPair] = useState('parent1');       // 1re moitié — années paires
  const [impair, setImpair] = useState('parent2');   // 1re moitié — années impaires
  const [veille, setVeille] = useState(true);
  const [err, setErr] = useState('');
  const [pick, setPick] = useState(null);            // 'debut' | 'fin' | null

  useEffect(() => {
    if (!visible) return;
    const p1 = init?.premiereMoitieGarde || 'parent1';
    setDebut(init?.debut || '');
    setFin(init?.fin || '');
    setPair(p1);
    setImpair(init?.alternerAnneeImpaire ? autre(p1) : p1);
    setVeille(init?.debutVeille !== false);
    setErr('');
  }, [visible, init]);

  function valider() {
    if (!debut) { setErr(t('garde.choisisDateDebut')); return; }
    const schema = {
      debut, fin: fin || null,
      premiereMoitieGarde: pair,
      deuxiemeMoitieGarde: autre(pair),
      alternerAnneeImpaire: impair !== pair,
      debutVeille: veille,
    };
    const r = onValider?.(schema);
    if (r && r.ok === false) { setErr(r.err ? t(r.err, r.errP) : t('messagerie.enregistrerImpossible')); return; }
    onFermer?.();
  }

  const action = { icone: mode === 'add' ? '＋' : '💾', onPress: valider, disabled: !debut, label: mode === 'add' ? t('commun.ajouter') : t('commun.enregistrer') };

  return (
   <>
    <Feuille visible={visible} titre={mode === 'add' ? t('garde.nouvelleGardeVacances') : t('garde.modifierGardeVacances')}
      emoji="🏖️" hauteur={0.82} onFermer={onFermer} action={action}>
      <Text style={s.lbl}>{t('garde.aPartirDu')}</Text>
      <TouchableOpacity style={s.champBtn} onPress={() => setPick('debut')}>
        <Text style={[s.champVal, !debut && s.champVide]}>📅 {debut ? frJMA(debut) : t('ft.choisir')}</Text>
      </TouchableOpacity>

      <Text style={s.lbl}>{t('garde.jusquAu')}</Text>
      <TouchableOpacity style={s.champBtn} onPress={() => setPick('fin')}>
        <Text style={[s.champVal, !fin && s.champVide]}>📅 {fin ? frJMA(fin) : t('finances.rrSansFin')}</Text>
      </TouchableOpacity>
      <Text style={s.aide}>{t('garde.laisseSansFinAide')}</Text>

      <Text style={s.lbl}>{t('garde.premiereMoitiePaires')}</Text>
      <ChoixPuces pleine value={pair} onChange={setPair} options={opts} />
      <Text style={s.lbl}>{t('garde.premiereMoitieImpaires')}</Text>
      <ChoixPuces pleine value={impair} onChange={setImpair} options={opts} />
      <Text style={s.aide}>
        {pair === impair
          ? t('garde.pasAlternanceAide', { nom: nomG(pair) })
          : t('garde.alternanceAide')}
      </Text>

      <View style={s.veilleRow}>
        <View style={{ flex: 1 }}>
          <Text style={s.veilleTxt}>{t('garde.gardeCommenceVeille')}</Text>
          <Text style={s.veilleSub}>{t('garde.gardeCommenceVeilleSub')}</Text>
        </View>
        <Bascule valeur={veille} onChange={setVeille} accessibilityLabel={t('garde.gardeCommenceVeille')} />
      </View>

      {!!err && <Text style={s.err}>{err}</Text>}
    </Feuille>

    <SelecteurDate visible={pick === 'debut'} titre={t('garde.dateDebut')} valeur={debut}
      onChange={(dk) => { setDebut(dk); setErr(''); }} onFermer={() => setPick(null)} />
    <SelecteurDate visible={pick === 'fin'} titre={t('finances.dateFin')} valeur={fin} min={debut} avecEffacer
      onChange={(dk) => { setFin(dk); setErr(''); }} onFermer={() => setPick(null)} />
   </>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  lbl: { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 14, marginBottom: 7 },
  champBtn: { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 12, borderWidth: 0.5, borderColor: UI.champBord },
  champVal: { fontSize: 15, color: UI.ink, fontWeight: '600' },
  champVide: { color: UI.faint, fontWeight: '400' },
  aide: { fontSize: 11.5, color: UI.faint, marginTop: 7, lineHeight: 16 },
  veilleRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 16 },
  veilleTxt: { fontSize: 13, fontWeight: '700', color: UI.ink },
  veilleSub: { fontSize: 11, color: UI.faint, marginTop: 1 },
  err: { fontSize: 12, color: '#C62828', marginTop: 12 },
});
